<template>
  <div class="campus-transfer-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center flex-wrap">
        <h4 class="mb-0">Campus Transfer Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading campus transfer records...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Campus Transfer Report</span>
              <span v-if="transferTypeLabel" class="filter-chip">
                {{ transferTypeLabel }}
              </span>
              <span class="date-range-text">
                <strong>{{ periodLabel }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!reportItems.length" class="text-center py-5 no-data-block">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p>No campus transfer records found for the selected criteria</p>
      </div>

      <div v-else class="table-container">
        <table class="report-table">
          <thead>
            <tr>
              <th class="text-center col-sno">#</th>
              <th class="text-center col-gr">GR. NO</th>
              <th class="text-left col-name">STUDENT NAME</th>
              <th class="text-center col-type">TYPE</th>
              <th class="text-left col-change">CLASS / SEC CHANGE</th>
              <th class="text-left col-details">DETAILS</th>
              <th class="text-center col-time">UPDATED</th>
              <th class="text-left col-by">UPDATED BY</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in sortedReportItems"
              :key="item.studentID + '-' + item.updatedOn + '-' + index"
            >
              <td class="text-center font-weight-bold">{{ index + 1 }}</td>
              <td class="text-center gr-cell">{{ item.grNo || "-" }}</td>
              <td class="text-left name-cell">
                {{ displayName(item.name) }}
              </td>
              <td class="text-center">
                <span
                  class="type-badge"
                  :class="'type-badge--' + (item.transferType || 'unknown')"
                >
                  {{ formatTransferType(item.transferType) }}
                </span>
              </td>
              <td class="text-left">
                <div
                  v-if="getParsed(item).classFrom || getParsed(item).classTo"
                  class="change-cell"
                >
                  <span class="change-from">{{
                    getParsed(item).classFrom || "—"
                  }}</span>
                  <span class="change-arrow">→</span>
                  <span class="change-to">{{
                    getParsed(item).classTo || "—"
                  }}</span>
                </div>
                <span
                  v-else-if="item.meta_class || classSectionLabel(item)"
                  class="current-class"
                >
                  {{ item.meta_class || classSectionLabel(item) }}
                </span>
                <span v-else class="muted-dash">—</span>
              </td>
              <td class="text-left details-cell">{{ item.details || "-" }}</td>
              <td class="text-center date-cell">
                <div class="date-stack">
                  <span class="date-main">{{
                    formatDatePart(item.updatedOn)
                  }}</span>
                  <span class="date-time">{{
                    formatTimePart(item.updatedOn)
                  }}</span>
                </div>
              </td>
              <td class="text-left by-cell">{{ item.metaUpdatedBy || "-" }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="page-footer">
        <small
          >Powered by MySkool.app | Generated On: {{ currentDateTime }}</small
        >
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import { parseCampusTransferDetails } from "@/utils/campusTransferReport";

export default {
  name: "CampusTransferPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      campusID: "",
      fromDate: "",
      toDate: "",
      transferType: "",
      parsedCache: {},
    };
  },
  computed: {
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    transferTypeLabel() {
      if (this.transferType === "campus") return "Campus transfers only";
      if (this.transferType === "inner") return "Inner transfers only";
      return "";
    },
    periodLabel() {
      if (this.fromDate && this.toDate) {
        return `${this.formatDate(this.fromDate)} to ${this.formatDate(
          this.toDate
        )}`;
      }
      if (this.fromDate) return `From ${this.formatDate(this.fromDate)}`;
      if (this.toDate) return `Till ${this.formatDate(this.toDate)}`;
      return `Generated on ${this.currentDateTime}`;
    },
    sortedReportItems() {
      return [...this.reportItems].sort(
        (a, b) =>
          this.dateSortValue(b.updatedOn) - this.dateSortValue(a.updatedOn)
      );
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || "";
      this.campusID = query.campusID || query.cID || "";
      this.fromDate = query.dtFrom || query.fromDate || "";
      this.toDate = query.dtTo || query.toDate || "";
      this.transferType = query.transferType || "";

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },

    getParsed(item) {
      const cacheKey = item.id || item.studentID || item.details;
      if (!this.parsedCache[cacheKey]) {
        this.parsedCache[cacheKey] = parseCampusTransferDetails(item.details);
      }
      return this.parsedCache[cacheKey];
    },

    displayName(value) {
      if (!value) return "-";
      return String(value).toUpperCase();
    },

    classSectionLabel(item) {
      const cls = item.className || "";
      const sec = item.sectionName || "";
      if (cls && sec) return `${cls} - ${sec}`;
      return cls || sec || "-";
    },

    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return date;
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatDatePart(date) {
      if (!date) return "-";
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return "-";
      return d.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatTimePart(date) {
      if (!date) return "";
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatTransferType(value) {
      if (value === "campus") return "Campus";
      if (value === "inner") return "Inner";
      if (!value) return "-";
      return value.charAt(0).toUpperCase() + value.slice(1);
    },

    dateSortValue(date) {
      if (!date) return 0;
      const d = new Date(date);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    },

    async loadCampusInfo() {
      try {
        const cId =
          this.campusID && Number(this.campusID) > 0
            ? this.campusID
            : this.$store.state.userData.cId;
        const response = await axios.get(
          `https://apiv26.myskool.app/api/campuses`,
          {
            params: { db: this.db, cId },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const campusIdNum = Number(this.campusID);
          if (!campusIdNum) {
            this.campusName =
              response.data.data[0]?.name ||
              this.$store.state.currentBranch?.name ||
              "MY SKOOL";
            this.campusBranch = "All Campuses";
            this.schoolLogo = response.data.data[0]?.logo || null;
            return;
          }

          const match = response.data.data.find((c) => c.id == campusIdNum);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed", e);
      }
    },

    buildApiUrl() {
      let url = `${this.$store.state.domain}StudentStaffDataLog/CampusTransferReport?db=${this.db}&type=student`;
      if (this.campusID && Number(this.campusID) > 0) {
        url += `&campusID=${this.campusID}`;
      }
      if (this.fromDate) {
        url += `&dtFrom=${this.fromDate}`;
      }
      if (this.toDate) {
        url += `&dtTo=${this.toDate}`;
      }
      if (this.transferType) {
        url += `&transferType=${this.transferType}`;
      }
      return url;
    },

    async loadReportData() {
      this.loading = true;
      this.error = null;
      this.parsedCache = {};

      try {
        const response = await axios.get(this.buildApiUrl(), {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        const payload = response.data;
        if (payload && payload.status === "success") {
          this.reportItems = Array.isArray(payload.data) ? payload.data : [];
        } else if (Array.isArray(payload)) {
          this.reportItems = payload;
        } else if (payload && Array.isArray(payload.data)) {
          this.reportItems = payload.data;
        } else {
          this.reportItems = [];
          this.error =
            (payload && payload.message) || "Failed to retrieve report data.";
        }
      } catch (err) {
        console.error("Error fetching campus transfer report:", err);
        this.error =
          "Failed to retrieve report data. Please ensure the API is running.";
      } finally {
        this.loading = false;
      }
    },

    print() {
      window.print();
    },

    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.campus-transfer-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1rem 2rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.2rem;
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn-modern {
  display: flex;
  align-items: center;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(108, 92, 231, 0.3);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;
  &:hover {
    color: #e74c3c;
    border-color: #fab1a0;
  }
}

.report-page {
  background: white;
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  max-width: 1480px;
  margin: 0 auto;
}

.report-header-modern {
  border-bottom: 3px solid #111;
  padding-bottom: 20px;

  .school-logo-img {
    height: 90px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 1.8rem;
    font-weight: 800;
    color: #000;
  }

  .report-info-line {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
  }

  .report-name-text {
    font-weight: 700;
    color: #6c5ce7;
  }

  .filter-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.65rem;
    border-radius: 999px;
    background: rgba(108, 92, 231, 0.1);
    color: #6c5ce7;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .date-range-text {
    color: #636e72;
  }
}

.report-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 10px 12px;
    border: 1px solid #dee2e6;
    font-size: 0.82rem;
    vertical-align: middle;
  }

  thead th {
    background: #2c3e50;
    color: white;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.03em;
  }

  tbody tr:nth-child(even) {
    background-color: #fafbfd;
  }

  .col-sno {
    width: 4%;
  }
  .col-gr {
    width: 8%;
  }
  .col-name {
    width: 16%;
  }
  .col-type {
    width: 7%;
  }
  .col-change {
    width: 14%;
  }
  .col-details {
    width: 28%;
  }
  .col-time {
    width: 11%;
  }
  .col-by {
    width: 12%;
  }

  .gr-cell {
    font-weight: 700;
    color: #6c5ce7;
  }

  .name-cell {
    font-weight: 700;
  }

  .by-cell {
    font-size: 0.78rem;
    color: #4b5563;
    word-break: break-word;
  }

  .details-cell {
    line-height: 1.45;
    word-break: break-word;
    color: #374151;
  }
}

.type-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  &.type-badge--campus {
    background: rgba(59, 130, 246, 0.12);
    color: #2563eb;
  }

  &.type-badge--inner {
    background: rgba(245, 158, 11, 0.14);
    color: #d97706;
  }

  &.type-badge--unknown {
    background: #f3f4f6;
    color: #6b7280;
  }
}

.change-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  line-height: 1.35;
}

.change-from {
  color: #9ca3af;
  text-decoration: line-through;
  font-weight: 600;
}

.change-arrow {
  color: #6c5ce7;
  font-weight: 800;
}

.change-to {
  color: #059669;
  font-weight: 700;
}

.current-class {
  color: #374151;
  font-weight: 600;
}

.muted-dash {
  color: #9ca3af;
}

.date-cell {
  white-space: nowrap;
}

.date-stack {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.date-main {
  font-weight: 700;
  color: #111827;
  font-size: 0.78rem;
}

.date-time {
  font-size: 0.72rem;
  color: #6b7280;
}

.no-data-block {
  color: #95a5a6;
}

.page-footer {
  text-align: center;
  margin-top: 2rem;
  color: #95a5a6;
  border-top: 1px solid #eee;
  padding-top: 1rem;
}

@media (max-width: 991px) {
  .campus-transfer-report {
    padding: 1rem;
  }
}

@media (max-width: 575px) {
  .campus-transfer-report {
    padding: 1rem;
  }

  .report-page {
    padding: 1rem;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
  }

  .campus-transfer-report {
    padding: 0 !important;
    background: white !important;
  }

  .report-page {
    box-shadow: none !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .report-table {
    font-size: 7.5pt !important;

    thead th {
      background: #eee !important;
      color: black !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tbody tr {
      break-inside: avoid;
    }
  }

  .type-badge,
  .change-to {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
